import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { useAuth, SignUpButton } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  LucideArrowLeft,
  LucideEye,
  LucideEyeOff,
  LucideDownload,
  LucideChevronLeft,
  LucideChevronRight,
  LucideCheck,
  LucideFilePlus,
  LucideLayoutGrid,
  LucideUsers,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { fill as fillPDFProvincial } from "./fill";
import templateUrl from "./template.pdf";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
import { BACKEND_URL } from "@/constants/api";
import { PRESETS } from "./presets";
import { LSWH_COLOR_MAP } from "../shared/colorSystem";
import { PillSelector } from "../shared/PillSelector";
import { PDFPreview } from "../shared/PDFPreview";
import { SectionHeading } from "../shared/SectionHeading";
import { AIGeneratedTextarea } from "../shared/AIGeneratedTextarea";
import { FormFieldGrid } from "../shared/FormFieldGrid";
import {
  selectItems,
  twoPlans,
  threePlans,
  fourPlans,
  frenchPlans,
  ersCompleted as ersCompletedOpts,
  ersBenchmark as ersBenchmarkOpts,
} from "./fields";

// COLOR_CLASSES and LSWH_COLOR_MAP are imported from ../shared/colorSystem

// ─── Step definitions ─────────────────────────────────────────────────────────

// All 11 logical steps (used for STEP_FIELDS completion tracking)
const STEPS = [
  { id: 1,  title: "Student"    },
  { id: 2,  title: "School"     },
  { id: 3,  title: "Skills"     },
  { id: 4,  title: "Language"   },
  { id: 5,  title: "French"     },
  { id: 6,  title: "Math"       },
  { id: 7,  title: "Science"    },
  { id: 8,  title: "Social St." },
  { id: 9,  title: "Health/PE"  },
  { id: 10, title: "Arts"       },
  { id: 11, title: "ERS"        },
];

// Subject step IDs — steps 4–9 are owned by the "Subjects" parent node
const SUBJECT_STEP_IDS = new Set([4, 5, 6, 7, 8, 9]);

// Top-level indicator nodes (6 nodes — Subjects is a parent for 4–9)
const TOP_STEPS = [
  { id: 1,          label: "Student"  },
  { id: 2,          label: "School"   },
  { id: 3,          label: "Skills"   },
  { id: "subjects", label: "Subjects", stepIds: [4, 5, 6, 7, 8, 9], jumpTo: 4 },
  { id: 10,         label: "Arts"     },
  { id: 11,         label: "ERS"      },
];

// Sub-row nodes — always visible beneath the top row
const SUB_STEPS = [
  { id: 4, label: "Language" },
  { id: 5, label: "French"   },
  { id: 6, label: "Math"     },
  { id: 7, label: "Science"  },
  { id: 8, label: "Social"   },
  { id: 9, label: "H&PE"     },
];

const STEP_FIELDS = {
  1:  ["studentName", "oen", "date", "grade", "teacher"],
  2:  ["board", "school", "daysAbsent", "totalDaysAbsent", "timesLate", "totalTimesLate"],
  3:  ["responsibility1","organization1","independentWork1","collaboration1","initiative1","selfRegulation1",
       "responsibility2","organization2","independentWork2","collaboration2","initiative2","selfRegulation2",
       "strengthsNextStepsLSWH"],
  4:  ["languagePlan", "languageGrade1", "languageGrade2", "languageComments"],
  5:  ["frenchPlan", "frenchListeningGrade1", "frenchListeningGrade2"],
  6:  ["mathematicsPlan", "mathematicsGrade1", "mathematicsGrade2", "mathematicsComments"],
  7:  ["scienceAndTechnologyPlan", "scienceAndTechnologyGrade1", "scienceAndTechnologyGrade2"],
  8:  ["socialStudiesPlan", "socialStudiesGrade1", "socialStudiesGrade2"],
  9:  ["healthEducationGrade1", "healthEducationGrade2", "physicalEducationGrade1", "physicalEducationGrade2"],
  10: ["artsHeader", "arts1Grade1", "arts1Grade2"],
  11: ["ersCompleted", "ersBenchmark"],
};

// Schema field → representative PDF widget name for yellow highlight
const SCHEMA_TO_PDF_FIELD = {
  date: "date", studentName: "studentName1", oen: "oen1", grade: "grade1",
  gradeInSeptember: "gradeInSeptember", board: "board", school: "school",
  address1: "address1", address2: "address2", principal: "principal",
  telephone: "telephone", teacher: "teacher",
  daysAbsent: "daysAbsent", totalDaysAbsent: "totalDaysAbsent",
  timesLate: "timesLate", totalTimesLate: "totalTimesLate",
  responsibility1: "responsibility1", responsibility2: "responsibility2",
  organization1: "organization1", organization2: "organization2",
  independentWork1: "independentWork1", independentWork2: "independentWork2",
  collaboration1: "collaboration1", collaboration2: "collaboration2",
  initiative1: "initiative1", initiative2: "initiative2",
  selfRegulation1: "selfRegulation1", selfRegulation2: "selfRegulation2",
  strengthsNextStepsLSWH: "strengthsNextStepsForImprovementLSWH",
  languagePlan: "languageESLELD", languageGrade1: "languageGrade1", languageGrade2: "languageGrade2",
  languageComments: "languageComments",
  nativeLanguage: "nativeLanguage", nativeLanguagePlan: "nativeLanguageESLELD",
  nativeLanguageGrade1: "nativeLanguageGrade1", nativeLanguageGrade2: "nativeLanguageGrade2",
  nativeLanguageComments: "nativeLanguageComments",
  frenchPlan: "frenchNA",
  frenchListeningPlan: "frenchListeningESLELD", frenchListeningGrade1: "frenchListeningGrade1", frenchListeningGrade2: "frenchListeningGrade2",
  frenchSpeakingPlan: "frenchSpeakingESLELD",  frenchSpeakingGrade1: "frenchSpeakingGrade1",  frenchSpeakingGrade2: "frenchSpeakingGrade2",
  frenchReadingPlan: "frenchReadingESLELD",    frenchReadingGrade1: "frenchReadingGrade1",    frenchReadingGrade2: "frenchReadingGrade2",
  frenchWritingPlan: "frenchWritingESLELD",    frenchWritingGrade1: "frenchWritingGrade1",    frenchWritingGrade2: "frenchWritingGrade2",
  frenchComments: "frenchComments",
  mathematicsPlan: "mathematicsESLELD", mathematicsGrade1: "mathematicsGrade1", mathematicsGrade2: "mathematicsGrade2",
  mathematicsComments: "mathematicsComments",
  scienceAndTechnologyPlan: "scienceAndTechnologyESLELD", scienceAndTechnologyGrade1: "scienceAndTechnologyGrade1",
  scienceAndTechnologyGrade2: "scienceAndTechnologyGrade2", scienceAndTechnologyComments: "scienceAndTechnologyComments",
  socialStudiesPlan: "socialStudiesESLELD", socialStudiesGrade1: "socialStudiesGrade1",
  socialStudiesGrade2: "socialStudiesGrade2", socialStudiesComments: "socialStudiesComments",
  healthEducationPlan: "healthEducationESLELD", healthEducationGrade1: "healthEducationGrade1", healthEducationGrade2: "healthEducationGrade2",
  physicalEducationPlan: "physicalEducationESLELD", physicalEducationGrade1: "physicalEducationGrade1", physicalEducationGrade2: "physicalEducationGrade2",
  healthAndPhysicalEducationComments: "healthAndPhysicalEducationComments",
  artsHeader: "artsHeader",
  arts1: "arts1", arts1Plan: "arts1ESLELD", arts1Grade1: "arts1Grade1", arts1Grade2: "arts1Grade2",
  arts2: "arts2", arts2Plan: "arts2ESLELD", arts2Grade1: "arts2Grade1", arts2Grade2: "arts2Grade2",
  arts3: "arts3", arts3Plan: "arts3ESLELD", arts3Grade1: "arts3Grade1", arts3Grade2: "arts3Grade2",
  arts4: "arts4", arts4Plan: "arts4ESLELD", arts4Grade1: "arts4Grade1", arts4Grade2: "arts4Grade2",
  optionalStudies: "optionalStudies", optionalStudiesPlan: "optionalStudiesESLELD",
  optionalStudiesGrade1: "optionalStudiesGrade1", optionalStudiesGrade2: "optionalStudiesGrade2",
  optionalStudiesComments: "optionalStudiesComments",
  ersCompleted: "ersCompletedYes", ersDateYear: "ersDateYear",
  ersDateMonth: "ersDateMonth", ersDateDay: "ersDateDay",
  ersBenchmark: "ersBenchmarkYes",
};

// PDF page index (0-based) for each schema field
const PAGE_INDEX_FOR_SCHEMA_FIELD = {
  date: 0, studentName: 0, oen: 0, grade: 0, gradeInSeptember: 0, teacher: 0,
  board: 0, school: 0, address1: 0, address2: 0, principal: 0, telephone: 0,
  daysAbsent: 0, totalDaysAbsent: 0, timesLate: 0, totalTimesLate: 0,
  responsibility1: 0, responsibility2: 0, organization1: 0, organization2: 0,
  independentWork1: 0, independentWork2: 0, collaboration1: 0, collaboration2: 0,
  initiative1: 0, initiative2: 0, selfRegulation1: 0, selfRegulation2: 0,
  strengthsNextStepsLSWH: 0,
  languagePlan: 1, languageGrade1: 1, languageGrade2: 1, languageComments: 1,
  nativeLanguage: 1, nativeLanguagePlan: 1, nativeLanguageGrade1: 1, nativeLanguageGrade2: 1, nativeLanguageComments: 1,
  frenchPlan: 1, frenchListeningPlan: 1, frenchListeningGrade1: 1, frenchListeningGrade2: 1,
  frenchSpeakingPlan: 1, frenchSpeakingGrade1: 1, frenchSpeakingGrade2: 1,
  frenchReadingPlan: 1, frenchReadingGrade1: 1, frenchReadingGrade2: 1,
  frenchWritingPlan: 1, frenchWritingGrade1: 1, frenchWritingGrade2: 1,
  frenchComments: 1,
  mathematicsPlan: 1, mathematicsGrade1: 1, mathematicsGrade2: 1, mathematicsComments: 1,
  scienceAndTechnologyPlan: 1, scienceAndTechnologyGrade1: 1, scienceAndTechnologyGrade2: 1, scienceAndTechnologyComments: 1,
  socialStudiesPlan: 2, socialStudiesGrade1: 2, socialStudiesGrade2: 2, socialStudiesComments: 2,
  healthEducationPlan: 2, healthEducationGrade1: 2, healthEducationGrade2: 2,
  physicalEducationPlan: 2, physicalEducationGrade1: 2, physicalEducationGrade2: 2,
  healthAndPhysicalEducationComments: 2,
  artsHeader: 2, arts1: 2, arts1Plan: 2, arts1Grade1: 2, arts1Grade2: 2,
  arts2: 2, arts2Plan: 2, arts2Grade1: 2, arts2Grade2: 2,
  arts3: 2, arts3Plan: 2, arts3Grade1: 2, arts3Grade2: 2,
  arts4: 2, arts4Plan: 2, arts4Grade1: 2, arts4Grade2: 2,
  artsComments: 2,
  optionalStudies: 2, optionalStudiesPlan: 2, optionalStudiesGrade1: 2, optionalStudiesGrade2: 2, optionalStudiesComments: 2,
  ersCompleted: 2, ersDateYear: 2, ersDateMonth: 2, ersDateDay: 2, ersBenchmark: 2,
};

// ─── Form schema ──────────────────────────────────────────────────────────────
const formSchema = z.object({
  date: z.string(), studentName: z.string(), oen: z.string(),
  grade: z.string(), gradeInSeptember: z.string(), teacher: z.string(),
  board: z.string(), school: z.string(), address1: z.string(), address2: z.string(),
  principal: z.string(), telephone: z.string(),
  daysAbsent: z.string(), totalDaysAbsent: z.string(),
  timesLate: z.string(), totalTimesLate: z.string(),
  responsibility1: z.string(), organization1: z.string(), independentWork1: z.string(),
  collaboration1: z.string(), initiative1: z.string(), selfRegulation1: z.string(),
  responsibility2: z.string(), organization2: z.string(), independentWork2: z.string(),
  collaboration2: z.string(), initiative2: z.string(), selfRegulation2: z.string(),
  strengthsNextStepsLSWH: z.string(),
  languagePlan: z.string(), languageGrade1: z.string(), languageGrade2: z.string(), languageComments: z.string(),
  nativeLanguage: z.string(), nativeLanguagePlan: z.string(),
  nativeLanguageGrade1: z.string(), nativeLanguageGrade2: z.string(), nativeLanguageComments: z.string(),
  frenchPlan: z.string(),
  frenchListeningPlan: z.string(), frenchListeningGrade1: z.string(), frenchListeningGrade2: z.string(),
  frenchSpeakingPlan: z.string(),  frenchSpeakingGrade1: z.string(),  frenchSpeakingGrade2: z.string(),
  frenchReadingPlan: z.string(),   frenchReadingGrade1: z.string(),   frenchReadingGrade2: z.string(),
  frenchWritingPlan: z.string(),   frenchWritingGrade1: z.string(),   frenchWritingGrade2: z.string(),
  frenchComments: z.string(),
  mathematicsPlan: z.string(), mathematicsGrade1: z.string(), mathematicsGrade2: z.string(), mathematicsComments: z.string(),
  scienceAndTechnologyPlan: z.string(), scienceAndTechnologyGrade1: z.string(),
  scienceAndTechnologyGrade2: z.string(), scienceAndTechnologyComments: z.string(),
  socialStudiesPlan: z.string(), socialStudiesGrade1: z.string(),
  socialStudiesGrade2: z.string(), socialStudiesComments: z.string(),
  healthEducationPlan: z.string(), healthEducationGrade1: z.string(), healthEducationGrade2: z.string(),
  physicalEducationPlan: z.string(), physicalEducationGrade1: z.string(), physicalEducationGrade2: z.string(),
  healthAndPhysicalEducationComments: z.string(),
  artsHeader: z.string(),
  arts1: z.string(), arts1Plan: z.string(), arts1Grade1: z.string(), arts1Grade2: z.string(),
  arts2: z.string(), arts2Plan: z.string(), arts2Grade1: z.string(), arts2Grade2: z.string(),
  arts3: z.string(), arts3Plan: z.string(), arts3Grade1: z.string(), arts3Grade2: z.string(),
  arts4: z.string(), arts4Plan: z.string(), arts4Grade1: z.string(), arts4Grade2: z.string(),
  artsComments: z.string(),
  optionalStudies: z.string(), optionalStudiesPlan: z.string(),
  optionalStudiesGrade1: z.string(), optionalStudiesGrade2: z.string(), optionalStudiesComments: z.string(),
  ersCompleted: z.string(), ersDateYear: z.string(), ersDateMonth: z.string(),
  ersDateDay: z.string(), ersBenchmark: z.string(),
});

// ─── Reusable primitives ──────────────────────────────────────────────────────
// PillSelector and SectionHeading are imported from ../shared/

// Compact grade box pair — renders two side-by-side grade inputs
function GradePair({ control, field1, field2, label1 = "Term 1", label2 = "Term 2" }) {
  return (
    <div className="flex items-end gap-3">
      <FormField
        control={control}
        name={field1}
        render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label1}</span>
            <FormControl>
              <input
                {...field}
                maxLength={2}
                className="w-12 h-11 text-center text-lg font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
              />
            </FormControl>
          </FormItem>
        )}
      />
      <div className="w-4 h-px bg-slate-200 mb-5" />
      <FormField
        control={control}
        name={field2}
        render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label2}</span>
            <FormControl>
              <input
                {...field}
                maxLength={2}
                className="w-12 h-11 text-center text-lg font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}

// Standard subject card: plan pills + compact grade pair + comments
function SubjectCard({ title, planField, grade1Field, grade2Field, commentsField, plans, nameField, control }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Subject header stripe */}
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {nameField && (
          <FormField
            control={control}
            name={nameField}
            render={({ field }) => (
              <FormItem className="mb-0">
                <FormControl>
                  <input
                    {...field}
                    placeholder="Subject name"
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white focus:border-teal-400 focus:outline-none w-40"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Plan */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
          <FormField
            control={control}
            name={planField}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PillSelector size="sm" value={field.value} onChange={field.onChange} options={plans} colorMap={{}} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Grades */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades</p>
          <GradePair control={control} field1={grade1Field} field2={grade2Field} />
        </div>

        {/* Comments */}
        {commentsField && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Comments</p>
            <FormField
              control={control}
              name={commentsField}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Teacher comments…" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Two-level step indicator: top row (6 nodes) + persistent subject sub-row
function StepIndicator({ currentStep, completedSteps, onStepClick }) {
  const isSubjectStep = SUBJECT_STEP_IDS.has(currentStep);

  return (
    <div>
      {/* ── Top row ───────────────────────────────────────────────────────── */}
      <div className="flex items-start w-full">
        {TOP_STEPS.map((step, index) => {
          const isSubjectsNode = step.id === "subjects";
          const isLast = index === TOP_STEPS.length - 1;
          const isDone = isSubjectsNode
            ? step.stepIds.every((id) => completedSteps.has(id))
            : completedSteps.has(step.id);
          const isCurrent = isSubjectsNode ? isSubjectStep : step.id === currentStep;

          const handleClick = () =>
            isSubjectsNode ? onStepClick(step.jumpTo) : onStepClick(step.id);

          return (
            <div key={String(step.id)} className="flex items-center" style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}>
              <div className="flex flex-col items-center" style={{ minWidth: "28px" }}>
                <button
                  type="button"
                  onClick={handleClick}
                  title={isSubjectsNode ? "Subjects" : String(step.label)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-all cursor-pointer shrink-0 ${
                    isDone && !isCurrent
                      ? "bg-teal-700 border-teal-700 text-white"
                      : isCurrent
                      ? "bg-teal-600 border-teal-600 text-white scale-110 ring-4 ring-inset ring-teal-400/50"
                      : "bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200"
                  }`}
                >
                  {isDone && !isCurrent ? (
                    <LucideCheck size={11} />
                  ) : isSubjectsNode ? (
                    <LucideLayoutGrid size={12} />
                  ) : (
                    step.id
                  )}
                </button>
                <span
                  className={`text-[9px] mt-0.5 text-center leading-tight ${
                    isCurrent ? "text-teal-700 font-bold" : isDone ? "text-teal-500 font-medium" : "text-slate-400 font-medium"
                  }`}
                  style={{ maxWidth: "44px", wordBreak: "break-word" }}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div className={`h-px flex-1 mx-0.5 mb-4 ${isDone ? "bg-teal-500" : "bg-slate-200"}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Subject sub-row ───────────────────────────────────────────────── */}
      {/* Smaller scale + inset card signals hierarchy without labeling it    */}
      <div className="mt-2 mx-1 rounded-xl bg-slate-50/80 border border-slate-100 px-4 py-2">
        <div className="flex items-start w-full">
          {SUB_STEPS.map((step, index) => {
            const isDone = completedSteps.has(step.id);
            const isCurrent = step.id === currentStep;
            const isLast = index === SUB_STEPS.length - 1;
            return (
              <div key={step.id} className="flex items-center" style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}>
                <div className="flex flex-col items-center" style={{ minWidth: "22px" }}>
                  <button
                    type="button"
                    onClick={() => onStepClick(step.id)}
                    title={step.label}
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-[1.5px] transition-all cursor-pointer shrink-0 ${
                      isDone && !isCurrent
                        ? "bg-teal-500 border-teal-500 text-white"
                        : isCurrent
                        ? "bg-teal-500 border-teal-500 text-white scale-125 ring-2 ring-inset ring-teal-300/50"
                        : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}
                  >
                    {isDone && !isCurrent ? <LucideCheck size={8} /> : step.id}
                  </button>
                  <span
                    className={`text-[8px] mt-0.5 text-center leading-tight ${
                      isCurrent ? "text-teal-600 font-bold" : isDone ? "text-teal-400 font-medium" : "text-slate-400 font-medium"
                    }`}
                    style={{ maxWidth: "36px", wordBreak: "break-word" }}
                  >
                    {step.label}
                  </span>
                </div>
                {!isLast && (
                  <div className={`h-px flex-1 mx-0.5 mb-3.5 ${isDone ? "bg-teal-300" : "bg-slate-200"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// PDFPreview is imported from ../shared/PDFPreview

// ─── Main component ───────────────────────────────────────────────────────────
export const FreeTrialReportCardProvincialV2 = ({ reportId, backUrl, contextInfo } = {}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [isGeneratingAI, setIsGeneratingAI] = useState({ lswh: false });
  const [aiError, setAiError] = useState(null);
  const [showCTA, setShowCTA] = useState(false);

  const getInitialPage = (step) => step <= 3 ? 1 : step <= 7 ? 2 : 3;
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: getInitialPage(s.id), topFraction: 0 }]))
  );

  const debounceRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});
  const lastChangedFieldRef = useRef(null);
  const { getToken } = useAuth();

  // One representative PDF field per step for scroll targeting
  const STEP_ANCHOR_FIELDS = {
    1: "studentName1", 2: "board",              3: "responsibility1",
    4: "languageGrade1", 5: "frenchListeningGrade1", 6: "mathematicsGrade1",
    7: "scienceAndTechnologyGrade1", 8: "socialStudiesGrade1",
    9: "healthEducationGrade1", 10: "artsHeader", 11: "ersCompletedYes",
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: Object.fromEntries(Object.keys(formSchema.shape).map((k) => [k, ""])),
  });

  // Restore saved data and generate initial PDF
  useEffect(() => {
    let initialValues;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = json.data && Object.keys(json.data).length > 0 ? json.data : form.getValues();
            form.reset(values);
            fillPDFProvincial(values).then((url) => setFilledPDF(url));
          })
          .catch(() => fillPDFProvincial(form.getValues()).then((url) => setFilledPDF(url)))
      );
      return;
    }
    const saved = localStorage.getItem("formData-pvrc-v2");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        form.reset(parsed);
        initialValues = parsed;
      } catch {
        initialValues = form.getValues();
      }
    } else {
      initialValues = form.getValues();
    }
    fillPDFProvincial(initialValues).then((url) => setFilledPDF(url));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build per-step scroll targets + per-field bounding boxes for yellow highlight
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = step <= 3 ? 0 : step <= 7 ? 1 : 2;
          try {
            const f = pdfForm.getField(fieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) throw new Error("no widgets");
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            updated[step] = { page: pageIdx + 1, topFraction: Math.min(0.95, topFromTop / pageH) };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        setScrollTargets((prev) => ({ ...prev, ...updated }));

        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = PAGE_INDEX_FOR_SCHEMA_FIELD[schemaField] ?? 0;
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error("Failed to build PDF scroll targets:", e);
      }
    }
    buildTargets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save (300 ms) + live PDF regeneration with highlight (500 ms)
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (reportId) {
          getToken().then((token) =>
            fetch(`${BACKEND_URL}api/reports/${reportId}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ data: form.getValues() }),
            })
          );
        } else {
          localStorage.setItem("formData-pvrc-v2", JSON.stringify(form.getValues()));
        }
      }, 300);

      if (name) lastChangedFieldRef.current = name;

      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      setIsPdfGenerating(true);
      regenDebounceRef.current = setTimeout(async () => {
        const changedField = lastChangedFieldRef.current;
        const highlightRect = changedField ? (fieldPositionsRef.current[changedField] ?? null) : null;
        const url = await fillPDFProvincial(form.getValues(), highlightRect);
        setFilledPDF(url);
        setIsPdfGenerating(false);
      }, 500);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleGenerateReportCard = async () => {
    const url = await fillPDFProvincial(form.getValues());
    setFilledPDF(url);
    setShowPreview(true);
  };

  const handleDownload = () => {
    if (!filledPDF) return;
    const a = document.createElement("a");
    a.href = filledPDF;
    a.setAttribute("download", "provincial-report-card.pdf");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    if (!reportId) setShowCTA(true);
  };

  const handleNext = () => setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  const handlePrev = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handleGenerateAI = async () => {
    setAiError(null);
    const token = await getToken();
    if (!token) { setAiError("Sign in to use AI comment generation."); return; }
    setIsGeneratingAI((p) => ({ ...p, lswh: true }));
    try {
      const v = form.getValues();
      const res = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          strengths: v.strengthsNextStepsLSWH || "",
          nextSteps: v.strengthsNextStepsLSWH || "",
          studentInfo: {
            grade: v.grade, absent: v.daysAbsent, late: v.timesLate,
            responsibility: v.responsibility1, organized: v.organization1,
            independent: v.independentWork1, collaboration: v.collaboration1,
            initiative: v.initiative1,
          },
        }),
      });
      if (res.status === 403) { setAiError("AI generation quota exceeded. Please upgrade your plan."); return; }
      if (!res.ok) { setAiError("AI generation requires a paid plan."); return; }
      const { comment } = await res.json();
      form.setValue("strengthsNextStepsLSWH", comment);
    } catch {
      setAiError("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGeneratingAI((p) => ({ ...p, lswh: false }));
    }
  };

  const watchedValues = form.watch();
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => watchedValues[f] && watchedValues[f] !== "")
    ).map((s) => s.id)
  );

  // ── Step content renderer ────────────────────────────────────────────────────
  const renderStep = () => {
    const { control } = form;

    // ── Step 1: Student Info ──────────────────────────────────────────────────
    if (currentStep === 1) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Student Information" />
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: "studentName",      label: "Student Name",       colSpan: 2 },
                { name: "oen",              label: "OEN"                            },
                { name: "date",             label: "Date"                           },
                { name: "grade",            label: "Grade",              small: true },
                { name: "gradeInSeptember", label: "Grade in Sept.",     small: true },
                { name: "teacher",          label: "Teacher",            colSpan: 2 },
              ].map(({ name, label, colSpan, small }) => (
                <FormField
                  key={name}
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormItem className={colSpan === 2 ? "col-span-2" : ""}>
                      <FormLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        {label}
                      </FormLabel>
                      <FormControl>
                        {small ? (
                          <input
                            {...field}
                            maxLength={3}
                            className="w-16 h-10 text-center text-base font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors block"
                          />
                        ) : (
                          <Input {...field} />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    // ── Step 2: School & Attendance ───────────────────────────────────────────
    if (currentStep === 2) {
      return (
        <div className="space-y-5">
          <SectionHeading title="School & Attendance" />

          {/* School info */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">School Details</p>
            <FormFieldGrid
              fields={[
                { name: "board",     label: "Board",          colSpan: 2 },
                { name: "school",    label: "School",         colSpan: 2 },
                { name: "address1",  label: "Address Line 1", colSpan: 2 },
                { name: "address2",  label: "Address Line 2", colSpan: 2 },
                { name: "principal", label: "Principal"                   },
                { name: "telephone", label: "Telephone"                   },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>

          {/* Attendance */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Attendance</p>
            <FormFieldGrid
              fields={[
                { name: "daysAbsent",      label: "Days Absent (Term)", inputMode: "numeric" },
                { name: "totalDaysAbsent", label: "Total Days Absent",  inputMode: "numeric" },
                { name: "timesLate",       label: "Times Late (Term)",  inputMode: "numeric" },
                { name: "totalTimesLate",  label: "Total Times Late",   inputMode: "numeric" },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
              gridClassName="grid grid-cols-2 gap-6"
            />
          </div>
        </div>
      );
    }

    // ── Step 3: Learning Skills (both terms) ──────────────────────────────────
    if (currentStep === 3) {
      const SKILLS = [
        { key: "responsibility",  label: "Responsibility"  },
        { key: "organization",    label: "Organization"    },
        { key: "independentWork", label: "Independent Work"},
        { key: "collaboration",   label: "Collaboration"   },
        { key: "initiative",      label: "Initiative"      },
        { key: "selfRegulation",  label: "Self-Regulation" },
      ];
      return (
        <div className="space-y-5">
          <SectionHeading title="Learning Skills & Work Habits" subtitle="Ratings for both terms" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {/* Column headers */}
            <div className="grid items-center border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-teal-600 text-center">Term 1</span>
              <span />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center">Term 2</span>
            </div>

            {/* Skill rows */}
            {SKILLS.map(({ key, label }, i) => (
              <div
                key={key}
                className={`grid items-center gap-3 px-5 py-3 ${i < SKILLS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}
              >
                <span className="text-sm font-semibold text-slate-700">{label}</span>

                {/* Term 1 */}
                <FormField
                  control={control}
                  name={`${key}1`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} colorMap={LSWH_COLOR_MAP} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Divider */}
                <div className="h-8 bg-slate-200" />

                {/* Term 2 */}
                <FormField
                  control={control}
                  name={`${key}2`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} colorMap={LSWH_COLOR_MAP} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>

          {/* LSWH comment */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <AIGeneratedTextarea
              control={control}
              name="strengthsNextStepsLSWH"
              label="Strengths & Next Steps — Learning Skills"
              isGenerating={isGeneratingAI.lswh}
              onGenerate={handleGenerateAI}
              placeholder="Teacher comments on learning skills…"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2"
              textareaClassName="min-h-[100px] resize-none text-sm"
              buttonClassName="border-teal-200 text-teal-700 hover:bg-teal-50"
            />
            {aiError && <p className="text-sm text-red-600 mt-2">{aiError}</p>}
          </div>
        </div>
      );
    }

    // ── Step 4: Language ──────────────────────────────────────────────────────
    if (currentStep === 4) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Language" />
          <SubjectCard
            title="Language"
            planField="languagePlan"
            grade1Field="languageGrade1"
            grade2Field="languageGrade2"
            commentsField="languageComments"
            plans={twoPlans}
            control={control}
          />
          <SubjectCard
            title="Native Language"
            nameField="nativeLanguage"
            planField="nativeLanguagePlan"
            grade1Field="nativeLanguageGrade1"
            grade2Field="nativeLanguageGrade2"
            commentsField="nativeLanguageComments"
            plans={twoPlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 5: French ────────────────────────────────────────────────────────
    if (currentStep === 5) {
      const FRENCH_SKILLS = [
        { label: "Listening", planField: "frenchListeningPlan", g1: "frenchListeningGrade1", g2: "frenchListeningGrade2" },
        { label: "Speaking",  planField: "frenchSpeakingPlan",  g1: "frenchSpeakingGrade1",  g2: "frenchSpeakingGrade2"  },
        { label: "Reading",   planField: "frenchReadingPlan",   g1: "frenchReadingGrade1",   g2: "frenchReadingGrade2"   },
        { label: "Writing",   planField: "frenchWritingPlan",   g1: "frenchWritingGrade1",   g2: "frenchWritingGrade2"   },
      ];
      return (
        <div className="space-y-4">
          <SectionHeading title="French" />

          {/* Program type */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">French Program Type</p>
            <FormField
              control={control}
              name="frenchPlan"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={frenchPlans} colorMap={{}} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Four skill rows */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="grid border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "100px 1fr auto" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Plan (ESL/IEP)</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">T1 / T2</span>
            </div>
            {FRENCH_SKILLS.map(({ label, planField, g1, g2 }, i) => (
              <div
                key={planField}
                className={`grid items-center gap-4 px-5 py-3 ${i < FRENCH_SKILLS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "100px 1fr auto" }}
              >
                <span className="text-sm font-semibold text-slate-700">{label}</span>
                <FormField
                  control={control}
                  name={planField}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector size="sm" value={field.value} onChange={field.onChange} options={twoPlans} colorMap={{}} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <FormField
                    control={control}
                    name={g1}
                    render={({ field }) => (
                      <FormItem className="flex flex-col items-center">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T1</span>
                        <FormControl>
                          <input
                            {...field}
                            maxLength={2}
                            className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name={g2}
                    render={({ field }) => (
                      <FormItem className="flex flex-col items-center">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T2</span>
                        <FormControl>
                          <input
                            {...field}
                            maxLength={2}
                            className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* French comments */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField
              control={control}
              name="frenchComments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Comments</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="French program comments…" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      );
    }

    // ── Step 6: Mathematics ───────────────────────────────────────────────────
    if (currentStep === 6) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Mathematics" />
          <SubjectCard
            title="Mathematics"
            planField="mathematicsPlan"
            grade1Field="mathematicsGrade1"
            grade2Field="mathematicsGrade2"
            commentsField="mathematicsComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 7: Science & Technology ─────────────────────────────────────────
    if (currentStep === 7) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Science & Technology" />
          <SubjectCard
            title="Science & Technology"
            planField="scienceAndTechnologyPlan"
            grade1Field="scienceAndTechnologyGrade1"
            grade2Field="scienceAndTechnologyGrade2"
            commentsField="scienceAndTechnologyComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 8: Social Studies ────────────────────────────────────────────────
    if (currentStep === 8) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Social Studies" />
          <SubjectCard
            title="Social Studies"
            planField="socialStudiesPlan"
            grade1Field="socialStudiesGrade1"
            grade2Field="socialStudiesGrade2"
            commentsField="socialStudiesComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 9: Health & PE ───────────────────────────────────────────────────
    if (currentStep === 9) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Health & Physical Education" />

          {/* Health Education */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Health Education</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
                <FormField control={control} name="healthEducationPlan" render={({ field }) => (
                  <FormItem><FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} colorMap={{}} />
                  </FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades</p>
                <GradePair control={control} field1="healthEducationGrade1" field2="healthEducationGrade2" />
              </div>
            </div>
          </div>

          {/* Physical Education */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Physical Education</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
                <FormField control={control} name="physicalEducationPlan" render={({ field }) => (
                  <FormItem><FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} colorMap={{}} />
                  </FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades</p>
                <GradePair control={control} field1="physicalEducationGrade1" field2="physicalEducationGrade2" />
              </div>
            </div>
          </div>

          {/* Shared H&PE Comments */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="healthAndPhysicalEducationComments" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">
                  H&amp;PE Comments
                </FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Health & Physical Education comments…" />
                </FormControl>
              </FormItem>
            )} />
          </div>
        </div>
      );
    }

    // ── Step 10: Arts & Optional Studies ─────────────────────────────────────
    if (currentStep === 10) {
      const ARTS_ROWS = [
        { nameField: "arts1", planField: "arts1Plan", g1: "arts1Grade1", g2: "arts1Grade2", placeholder: "e.g. Visual Arts" },
        { nameField: "arts2", planField: "arts2Plan", g1: "arts2Grade1", g2: "arts2Grade2", placeholder: "e.g. Music"       },
        { nameField: "arts3", planField: "arts3Plan", g1: "arts3Grade1", g2: "arts3Grade2", placeholder: "e.g. Drama"       },
        { nameField: "arts4", planField: "arts4Plan", g1: "arts4Grade1", g2: "arts4Grade2", placeholder: "e.g. Dance"       },
      ];
      return (
        <div className="space-y-4">
          <SectionHeading title="Arts" />

          {/* Arts header field */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="artsHeader" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Arts Header</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. The Arts" className="max-w-xs" />
                </FormControl>
              </FormItem>
            )} />
          </div>

          {/* Arts subjects — compact table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="grid border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "1fr 1fr auto" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Subject / Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Program Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">T1 / T2</span>
            </div>
            {ARTS_ROWS.map(({ nameField, planField, g1, g2, placeholder }, i) => (
              <div
                key={nameField}
                className={`grid items-center gap-4 px-5 py-3 ${i < ARTS_ROWS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "1fr 1fr auto" }}
              >
                <FormField control={control} name={nameField} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <input
                        {...field}
                        placeholder={placeholder}
                        className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:border-teal-400 focus:outline-none"
                      />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={control} name={planField} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="flex items-center gap-2">
                  <FormField control={control} name={g1} render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T1</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={control} name={g2} render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T2</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              </div>
            ))}
          </div>

          {/* Arts comments */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="artsComments" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Arts Comments</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Arts comments…" />
                </FormControl>
              </FormItem>
            )} />
          </div>

          {/* Optional Studies */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Optional Studies</span>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
                <FormField control={control} name="optionalStudies" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Subject Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Islamic Studies" />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={control} name="optionalStudiesPlan" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Program Plan</FormLabel>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="flex items-end gap-2 pb-0.5">
                  <FormField control={control} name="optionalStudiesGrade1" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T1</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={control} name="optionalStudiesGrade2" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T2</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              </div>
              <FormField control={control} name="optionalStudiesComments" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Comments</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[80px] resize-none text-sm" />
                  </FormControl>
                </FormItem>
              )} />
            </div>
          </div>
        </div>
      );
    }

    // ── Step 11: ERS ──────────────────────────────────────────────────────────
    if (currentStep === 11) {
      return (
        <div className="space-y-5">
          <SectionHeading
            title="Early Reading Strategy"
            subtitle="Record ERS assessment completion and benchmark status"
          />

          {/* Completed */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Assessment Completed</p>
            <FormField control={control} name="ersCompleted" render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex gap-3">
                    {ersCompletedOpts.map((opt) => {
                      const isClear = opt.value === "";
                      const isSelected = opt.value === field.value;
                      return (
                        <button
                          key={opt.value + opt.label}
                          type="button"
                          onClick={() => field.onChange(opt.value)}
                          className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-colors cursor-pointer ${
                            isSelected
                              ? isClear
                                ? "bg-slate-600 border-slate-600 text-white"
                                : opt.value === "Yes"
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : opt.value === "No"
                                    ? "bg-red-500 border-red-500 text-white"
                                    : "bg-slate-500 border-slate-500 text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {isClear ? "×" : opt.label}
                        </button>
                      );
                    })}
                  </div>
                </FormControl>
              </FormItem>
            )} />
          </div>

          {/* Date */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Assessment Date</p>
            <div className="flex items-end gap-3">
              <FormField control={control} name="ersDateYear" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Year</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="YYYY" maxLength={4} inputMode="numeric"
                      className="w-20 h-10 text-center font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
                    />
                  </FormControl>
                </FormItem>
              )} />
              <span className="text-slate-300 text-2xl font-light pb-1">/</span>
              <FormField control={control} name="ersDateMonth" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Month</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="MM" maxLength={2} inputMode="numeric"
                      className="w-14 h-10 text-center font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
                    />
                  </FormControl>
                </FormItem>
              )} />
              <span className="text-slate-300 text-2xl font-light pb-1">/</span>
              <FormField control={control} name="ersDateDay" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Day</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="DD" maxLength={2} inputMode="numeric"
                      className="w-14 h-10 text-center font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
                    />
                  </FormControl>
                </FormItem>
              )} />
            </div>
          </div>

          {/* Benchmark */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Benchmark Met</p>
            <FormField control={control} name="ersBenchmark" render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex gap-3">
                    {ersBenchmarkOpts.map((opt) => {
                      const isClear = opt.value === "";
                      const isSelected = opt.value === field.value;
                      return (
                        <button
                          key={opt.value + opt.label}
                          type="button"
                          onClick={() => field.onChange(opt.value)}
                          className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-colors cursor-pointer ${
                            isSelected
                              ? isClear
                                ? "bg-slate-600 border-slate-600 text-white"
                                : opt.value === "Yes"
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : opt.value === "No"
                                    ? "bg-red-500 border-red-500 text-white"
                                    : "bg-slate-500 border-slate-500 text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {isClear ? "×" : opt.label}
                        </button>
                      );
                    })}
                  </div>
                </FormControl>
              </FormItem>
            )} />
          </div>
        </div>
      );
    }

    return null;
  };

  const isShared = backUrl?.startsWith("/shared-classes");

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
        <Link to={backUrl || "/"}>
          <Button variant="ghost" size="sm">
            <LucideArrowLeft size={16} className="mr-1.5" />
            Back
          </Button>
        </Link>
        {contextInfo ? (
          <div className="text-center">
            <p className="text-xs text-slate-500 leading-none">
              {[contextInfo.grade && `Grade ${contextInfo.grade}`, contextInfo.schoolYear]
                .filter(Boolean).join(' · ')}
            </p>
            <p className="text-sm font-semibold text-slate-800 leading-tight mt-0.5">
              {[contextInfo.oen && `OEN ${contextInfo.oen}`, contextInfo.studentName]
                .filter(Boolean).join(' · ')}
            </p>
          </div>
        ) : (
          <h1 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Ontario Provincial Report Card
          </h1>
        )}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? <LucideEyeOff size={16} /> : <LucideEye size={16} />}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
            {isPdfGenerating ? <span className="text-xs">Updating…</span> : <LucideDownload size={16} />}
          </Button>
        </div>
      </div>

      {/* Shared-class banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          Shared class — changes you save are visible to the owner
        </div>
      )}

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div className={`flex flex-col overflow-hidden transition-all duration-300 ${showPreview ? "w-[55%]" : "w-full"}`}>
          {/* Step indicator */}
          <div className="px-5 pt-4 pb-2 shrink-0 border-b border-slate-100 bg-white">
            <StepIndicator
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
            />
          </div>

          {/* Free-trial preset toolbar */}
          {!reportId && (
            <div className="px-5 py-2 shrink-0 flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => form.reset(preset.data)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div className="flex-1 overflow-y-auto px-5 py-5 bg-slate-50">
            <Form {...form}>
              <form>{renderStep()}</form>
            </Form>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button type="button" variant="outline" onClick={handlePrev} disabled={currentStep === 1}>
              <LucideChevronLeft size={16} className="mr-1" />
              Prev
            </Button>
            {/* Generate PDF button hidden — PDF auto-fills on every change
            <Button
              type="button"
              variant="outline"
              onClick={handleGenerateReportCard}
              className="border-teal-600 text-teal-700 hover:bg-teal-50"
            >
              <LucideFilePlus size={16} className="mr-1.5" />
              Generate PDF
            </Button>
            */}
            {currentStep < STEPS.length ? (
              <Button type="button" onClick={handleNext}>
                Next
                <LucideChevronRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button type="button" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
                <LucideDownload size={16} className="mr-1.5" />
                {isPdfGenerating ? "Updating…" : "Download PDF"}
              </Button>
            )}
          </div>
        </div>

        {/* PDF preview panel */}
        <div className={`border-l border-slate-200 transition-all duration-300 overflow-hidden ${showPreview ? "w-[45%]" : "w-0"}`}>
          {showPreview && (
            <PDFPreview src={filledPDF} scrollTarget={scrollTargets[currentStep]} defaultPageCount={4} />
          )}
        </div>
      </div>

      {/* Sign-up CTA overlay — shown after first free-trial download */}
      {showCTA && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-t-xl p-6 max-w-md w-full space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">Your report card is ready!</h2>
            <p className="text-slate-600 text-sm">
              Sign up free to save this report, add more students, and access it from any device.
            </p>
            <SignUpButton mode="modal">
              <Button className="w-full">Sign Up Free</Button>
            </SignUpButton>
            <Button
              variant="ghost"
              className="w-full text-slate-500"
              onClick={() => setShowCTA(false)}
            >
              Continue without saving
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
